// eslint-disable-next-line react-refresh/only-export-components -- App is the root; boot screens live here intentionally
import { useCallback } from 'react'
import { Route, Switch, useLocation, useRoute } from 'wouter'
import type { NavTab } from '@/domain/types'
import { useAuthContext } from '@/auth/AuthProvider'
import { SiteHeader } from '@/components/layout/SiteHeader'
import { SiteFooter } from '@/components/layout/SiteFooter'
import { Hero } from '@/components/layout/Hero'
import { responsiveStyles } from '@/components/layout/responsive'
import { HomeView } from '@/views/HomeView'
import { ChatView } from '@/views/ChatView'
import { InitiativesView } from '@/views/InitiativesView'
import { InitiativeDetailView } from '@/views/InitiativeDetailView'
import { DomainsView } from '@/views/DomainsView'
import { DomainDetailView } from '@/views/DomainDetailView'
import { CapabilitiesView } from '@/views/CapabilitiesView'
import { CapabilityDetailView } from '@/views/CapabilityDetailView'
import { ChatProvider, useChatContext, chatIsActive } from '@/api/foundry/ChatProvider'
import { tokens } from '@/theme'

const globalStyles =
  `
@keyframes barGrow {
  from { transform: scaleX(0); opacity: 0; }
  to   { transform: scaleX(1); opacity: 1; }
}
@keyframes slideUp {
  from { transform: translateY(14px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
` + responsiveStyles

// Chat focus mode — the "Ask Orion" hero (ChatView) and the copyright footer
// (App shell) collapse when a conversation is active, so the thread fills the
// space between the sticky header and the composer. Injected once in
// AuthenticatedShell so the classes reach both. Grid-rows 1fr→0fr reclaims
// the space with no magic max-height; the inner wrapper clips and slides (hero
// up, footer down). Degrades to an instant collapse where grid-row animation
// isn't supported, and honours prefers-reduced-motion.
// See docs/sps/chat-focus-mode-requirements.md.
const focusModeStyles = `
.orion-collapse {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows ${tokens.durStd} ${tokens.easeStandard}, opacity ${tokens.durStd} ease;
}
.orion-collapse > .orion-collapse__inner {
  min-height: 0;
  overflow: hidden;
  transition: transform ${tokens.durStd} ${tokens.easeStandard};
}
.orion-collapse.is-collapsed { grid-template-rows: 0fr; opacity: 0; }
.orion-collapse.is-collapsed > .orion-collapse__inner { pointer-events: none; }
.orion-collapse--hero.is-collapsed > .orion-collapse__inner { transform: translateY(-18px); }
.orion-collapse--footer.is-collapsed > .orion-collapse__inner { transform: translateY(18px); }
@media (prefers-reduced-motion: reduce) {
  .orion-collapse, .orion-collapse > .orion-collapse__inner { transition: none; }
}
`

export default function App() {
  const { authState, signIn, signOut } = useAuthContext()

  if (authState.status === 'initialising') {
    return (
      <>
        <style>{globalStyles}</style>
        <BootScreen message="Starting…" />
      </>
    )
  }

  if (authState.status === 'unauthenticated') {
    return (
      <>
        <style>{globalStyles}</style>
        <SignInScreen onSignIn={() => void signIn()} />
      </>
    )
  }

  const { account, roles } = authState

  if (!roles.isReader) {
    return (
      <>
        <style>{globalStyles}</style>
        <NoRoleScreen onSignOut={() => void signOut()} />
      </>
    )
  }

  return (
    <>
      <style>{globalStyles}</style>
      <AuthenticatedShell
        userName={account.name ?? account.username}
        onSignOut={() => void signOut()}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Authenticated app — route-driven. Active tab is derived from the URL,
// not stored in React state. Detail views read their id from the route
// param via useRoute and pass routed callbacks for back/cross-link.
// ---------------------------------------------------------------------------

interface ShellProps {
  userName: string
  onSignOut: () => void
}

/**
 * The copyright footer, collapsed downward while a chat conversation is active
 * on /chat (chat focus mode). It lives inside ChatProvider — unlike
 * AuthenticatedShell, which *renders* the provider and so can't read chat
 * state itself — so it can key off the conversation. On every other route it
 * renders normally. See docs/sps/chat-focus-mode-requirements.md.
 */
function ChatAwareFooter() {
  const chat = useChatContext()
  const [location] = useLocation()
  const collapsed = location === '/chat' && chatIsActive(chat)
  return (
    <div
      className={`orion-collapse orion-collapse--footer${collapsed ? ' is-collapsed' : ''}`}
      aria-hidden={collapsed || undefined}
    >
      <div className="orion-collapse__inner">
        <SiteFooter />
      </div>
    </div>
  )
}

function AuthenticatedShell({ userName, onSignOut }: ShellProps) {
  const [location, setLocation] = useLocation()

  const activeTab = deriveActiveTab(location)

  const onTabChange = useCallback(
    (tab: NavTab) => {
      setLocation(tabToPath(tab))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [setLocation],
  )

  const openInitiative = useCallback(
    (id: number) => {
      setLocation(`/initiatives/${id}`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [setLocation],
  )

  const openDomain = useCallback(
    (id: number) => {
      setLocation(`/domains/${id}`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [setLocation],
  )

  const openCapability = useCallback(
    (id: number) => {
      setLocation(`/capabilities/${id}`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [setLocation],
  )

  return (
    // ChatProvider sits above the router so the chat hook (and its
    // in-flight SSE stream, message list, threadId, trusted-tools set)
    // survives navigation between routes. A user can kick off a chat
    // question, navigate to /initiatives to look something up, and
    // come back to /chat to find the answer waiting — no re-asking.
    <ChatProvider>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <style>{focusModeStyles}</style>
        <SiteHeader
          active={activeTab}
          onChange={onTabChange}
          userName={userName}
          onSignOut={onSignOut}
        />
        {/* Column flex so Chat can push its composer to the bottom of a
            short conversation; every other view is content-height. */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Switch>
            <Route path="/">
              <HomeRoute
                onOpenInitiative={openInitiative}
                onOpenCapabilities={() => onTabChange('capabilities')}
              />
            </Route>
            <Route path="/chat">
              <ChatView />
            </Route>
            <Route path="/initiatives">
              <InitiativesRoute onSelectInitiative={openInitiative} />
            </Route>
            <Route path="/initiatives/:id">
              <InitiativeDetailRoute
                onSelectDomain={openDomain}
                onSelectCapability={openCapability}
                onSelectInitiative={openInitiative}
              />
            </Route>
            <Route path="/domains">
              <DomainsView onSelectDomain={openDomain} />
            </Route>
            <Route path="/domains/:id">
              <DomainDetailRoute onSelectCapability={openCapability} />
            </Route>
            <Route path="/capabilities">
              <CapabilitiesRoute onSelectInitiative={openInitiative} />
            </Route>
            <Route path="/capabilities/:id">
              <CapabilityDetailRoute onSelectCapability={openCapability} />
            </Route>
            <Route>
              <NotFoundView onHome={() => setLocation('/')} />
            </Route>
          </Switch>
        </main>
        <ChatAwareFooter />
      </div>
    </ChatProvider>
  )
}

function deriveActiveTab(pathname: string): NavTab {
  if (pathname.startsWith('/initiatives')) return 'initiatives'
  if (pathname.startsWith('/domains')) return 'domains'
  if (pathname.startsWith('/capabilities')) return 'capabilities'
  if (pathname.startsWith('/chat')) return 'chat'
  return 'home'
}

function tabToPath(tab: NavTab): string {
  switch (tab) {
    case 'home':
      return '/'
    case 'chat':
      return '/chat'
    case 'initiatives':
      return '/initiatives'
    case 'domains':
      return '/domains'
    case 'capabilities':
      return '/capabilities'
  }
}

// ---------------------------------------------------------------------------
// Home adapter
//
// Lives inside ChatProvider so the hero's Ask Orion box can push a question
// straight into the same chat state ChatView renders. Asking from home
// therefore behaves exactly like typing on /chat — the answer streams into
// the shared thread, and the user lands on /chat to watch it arrive.
// ---------------------------------------------------------------------------

function HomeRoute({
  onOpenInitiative,
  onOpenCapabilities,
}: {
  onOpenInitiative: (id: number) => void
  onOpenCapabilities: () => void
}) {
  const chat = useChatContext()
  const askInChat = useAskInChat()

  return (
    <HomeView
      onOpenInitiative={onOpenInitiative}
      onOpenCapabilities={onOpenCapabilities}
      onAsk={askInChat}
      askUnavailable={chat.configMissing}
    />
  )
}

/**
 * Initiatives list. Its "raise an idea in chat" CTA is now the funnel's only
 * front door — the create modal is gone, and an initiative is born in a
 * conversation or not at all — so the handoff Home uses is what carries it.
 */
function InitiativesRoute({ onSelectInitiative }: { onSelectInitiative: (id: number) => void }) {
  const askInChat = useAskInChat()

  return <InitiativesView onSelectInitiative={onSelectInitiative} onRaiseIdea={askInChat} />
}

/**
 * Capabilities explorer. Both its CTAs — "raise an idea" in the hero and
 * "propose one in chat" on an empty capability — hand chat a seeded
 * question, so it needs the same chat handoff Home uses.
 */
function CapabilitiesRoute({ onSelectInitiative }: { onSelectInitiative: (id: number) => void }) {
  const askInChat = useAskInChat()

  return (
    <CapabilitiesView
      onSelectInitiative={onSelectInitiative}
      onProposeInitiative={capabilityName =>
        askInChat(
          `I have an idea for an initiative that would strengthen the ${capabilityName} capability — help me refine it.`,
        )
      }
      onRaiseIdea={askInChat}
    />
  )
}

/**
 * Push a question into the shared chat thread and route to /chat to watch
 * it stream. Asking from anywhere therefore behaves exactly like typing on
 * /chat — same thread, same audit trail.
 */
function useAskInChat(): (question: string) => void {
  const chat = useChatContext()
  const [, setLocation] = useLocation()

  return useCallback(
    (question: string) => {
      setLocation('/chat')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      void chat.send(question)
    },
    [chat, setLocation],
  )
}

// ---------------------------------------------------------------------------
// Detail-route adapters
//
// Each adapter parses :id from the route param and validates it. Bad IDs
// (non-numeric, or numeric routes that the API rejects with 404) are
// surfaced via the existing detail-view error states — not handled here.
// ---------------------------------------------------------------------------

function InitiativeDetailRoute({
  onSelectDomain,
  onSelectCapability,
  onSelectInitiative,
}: {
  onSelectDomain: (id: number) => void
  onSelectCapability: (id: number) => void
  onSelectInitiative: (id: number) => void
}) {
  const [, params] = useRoute<{ id: string }>('/initiatives/:id')
  const [, setLocation] = useLocation()
  const id = parseRouteId(params?.id)
  if (id === null) {
    return <InvalidRouteParam onHome={() => setLocation('/initiatives')} />
  }
  return (
    <InitiativeDetailView
      // Remount on id change. The detail view holds a refreshKey and
      // several child queries keyed off it; navigating from a fork to its
      // parent without remounting would show the parent with the fork's
      // refresh state.
      key={id}
      initiativeId={id}
      onBack={() => setLocation('/initiatives')}
      onSelectDomain={onSelectDomain}
      onSelectCapability={onSelectCapability}
      onSelectInitiative={onSelectInitiative}
    />
  )
}

function DomainDetailRoute({ onSelectCapability }: { onSelectCapability: (id: number) => void }) {
  const [, params] = useRoute<{ id: string }>('/domains/:id')
  const [, setLocation] = useLocation()
  const id = parseRouteId(params?.id)
  if (id === null) {
    return <InvalidRouteParam onHome={() => setLocation('/domains')} />
  }
  return (
    <DomainDetailView
      domainId={id}
      onBack={() => setLocation('/domains')}
      onSelectCapability={onSelectCapability}
    />
  )
}

function CapabilityDetailRoute({
  onSelectCapability,
}: {
  onSelectCapability: (id: number) => void
}) {
  const [, params] = useRoute<{ id: string }>('/capabilities/:id')
  const [, setLocation] = useLocation()
  const id = parseRouteId(params?.id)
  if (id === null) {
    return <InvalidRouteParam onHome={() => setLocation('/capabilities')} />
  }
  return (
    <CapabilityDetailView
      capabilityId={id}
      onBack={() => setLocation('/capabilities')}
      onSelectCapability={onSelectCapability}
    />
  )
}

function parseRouteId(raw: string | undefined): number | null {
  if (!raw) return null
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) return null
  return parsed
}

// ---------------------------------------------------------------------------
// Boot / sign-in / no-role / 404 / invalid-id screens
// ---------------------------------------------------------------------------

function BootScreen({ message }: { message: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: tokens.paper,
        fontFamily: tokens.fontDisplay,
        fontSize: 14,
        color: tokens.ink3,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}
    >
      {message}
    </div>
  )
}

function SignInScreen({ onSignIn }: { onSignIn: () => void }) {
  return (
    <>
      <Hero
        eyebrow="Trelleborg Industrial Solutions · Internal"
        headlineTop="Business context,"
        headlineBottom="in one place."
        lede="Orion connects domains, capabilities, and AI initiatives in a structured, auditable graph. Sign in with your Trelleborg account to continue."
      />
      <div
        className="orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `48px ${tokens.containerPadX}px`,
        }}
      >
        <button
          onClick={onSignIn}
          style={{
            background: tokens.gold,
            color: tokens.paper,
            border: 'none',
            borderRadius: tokens.radiusMd,
            padding: '12px 28px',
            fontFamily: tokens.fontDisplay,
            fontSize: 12,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Sign in with Microsoft
        </button>
      </div>
    </>
  )
}

function NoRoleScreen({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div
      className="orion-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '96px auto',
        padding: `0 ${tokens.containerPadX}px`,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          width: 48,
          height: 2,
          background: tokens.gold,
          marginBottom: 24,
        }}
      />
      <h1
        style={{
          fontSize: tokens.sizeH1,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          marginBottom: 16,
        }}
      >
        Access not configured
      </h1>
      <p
        style={{ fontSize: tokens.sizeBodyL, color: tokens.ink2, maxWidth: 540, lineHeight: 1.65 }}
      >
        Your account is authenticated but has not been assigned an Orion App Role. Contact your IT
        administrator to request access to the <strong>tis.orion.sec.reader</strong> (or higher)
        security group.
      </p>
      <p
        style={{
          fontSize: tokens.sizeBody,
          color: tokens.ink3,
          maxWidth: 540,
          lineHeight: 1.65,
          marginTop: 16,
        }}
      >
        If you have recently been granted access, sign out and sign back in to refresh your token.
      </p>
      <button
        onClick={onSignOut}
        style={{
          marginTop: 24,
          background: 'transparent',
          color: tokens.ink2,
          border: `1px solid ${tokens.ink4}`,
          borderRadius: tokens.radiusMd,
          padding: '10px 24px',
          fontFamily: tokens.fontDisplay,
          fontSize: 12,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        Sign out
      </button>
    </div>
  )
}

function NotFoundView({ onHome }: { onHome: () => void }) {
  return (
    <div
      className="orion-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '96px auto',
        padding: `0 ${tokens.containerPadX}px`,
        fontFamily: tokens.fontDisplay,
        textAlign: 'center',
      }}
    >
      <h1
        style={{
          fontSize: tokens.sizeH1,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          marginBottom: 16,
        }}
      >
        Page not found
      </h1>
      <p style={{ fontSize: tokens.sizeBodyL, color: tokens.ink2, marginBottom: 24 }}>
        The route you tried doesn&rsquo;t exist in Orion.
      </p>
      <button
        onClick={onHome}
        style={{
          background: tokens.gold,
          color: tokens.paper,
          border: 'none',
          borderRadius: tokens.radiusMd,
          padding: '10px 24px',
          fontFamily: tokens.fontDisplay,
          fontSize: 12,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        Back to home
      </button>
    </div>
  )
}

function InvalidRouteParam({ onHome }: { onHome: () => void }) {
  return (
    <div
      className="orion-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '96px auto',
        padding: `0 ${tokens.containerPadX}px`,
        fontFamily: tokens.fontDisplay,
        textAlign: 'center',
      }}
    >
      <h1
        style={{
          fontSize: tokens.sizeH2,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          marginBottom: 16,
        }}
      >
        Invalid identifier
      </h1>
      <p style={{ fontSize: tokens.sizeBody, color: tokens.ink2, marginBottom: 24 }}>
        The id in the URL isn&rsquo;t a positive integer.
      </p>
      <button
        onClick={onHome}
        style={{
          background: 'transparent',
          color: tokens.ink2,
          border: `1px solid ${tokens.ink4}`,
          borderRadius: tokens.radiusMd,
          padding: '10px 24px',
          fontFamily: tokens.fontDisplay,
          fontSize: 12,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        Back to list
      </button>
    </div>
  )
}
